import refreshAuthenticityToken, {
  getCsrfTokenFromDoc, resetPromise
} from "./refresh_authenticity_token"
import serializeParams from "./serialize_params"
import keysToCamel from "./keys_to_camel"
import errorReporter from "error_reporter"
import {v4 as uuidv4} from "uuid"

const ajax = {
  post: (url, data, options = {}) =>
    fetchWithBody(url, "POST", data, options),
  put: (url, data, options = {}) =>
    fetchWithBody(url, "PUT", data, options),
  delete: (url, options = {}) =>
    fetchWithBody(url, "DELETE", null, options),
  get: (url, data, options = {}) => {
    if (data) url = `${url}?${serializeParams(data)}`
    url = addApiVersionToUrl(url)
    options = {headers: {"Content-Type": "application/json"}, ...options}
    return fetch(url, options)
      .catch(handleConnectionFailure)
      .then((response) => handleResponse(response, {method: "GET"}))
  },
  getTurboStream: (url, options = {}) => {
    options = {headers: {Accept: "text/vnd.turbo-stream.html"}, ...options}
    return fetch(url, options)
      .catch(handleConnectionFailure)
      .then(response => response.text())
  }
}

async function fetchWithBody(url, method, data, options = {}) {
  url = addApiVersionToUrl(url)
  const body = data && JSON.stringify(data)
  const defaultHeaders = await ajaxHeaders()
  const headers = {...defaultHeaders, ...(options.headers || {})}
  const fetchOptions = {method, headers, body}
  const response = await fetchWithHeaders(url, fetchOptions)
  return handleResponse(response, fetchOptions)
}

function addApiVersionToUrl(url) {
  url = new URL(url, window.location)
  url.searchParams.set("version", 2)
  return url.toString()
}

async function fetchWithHeaders(url, options) {
  try {
    return await fetch(url, options)
  } catch (error) {
    return handleConnectionFailure(error)
  }
}

export async function ajaxHeaders() {
  const csrfToken = await refreshAuthenticityToken()
  return {
    "X-CSRF-Token": csrfToken,
    "Content-Type": "application/json",
    "Idempotency-Key": uuidv4(),
    Accept: "application/json"
  }
}

// inspired by https://gist.github.com/odewahn/5a5eeb23279eed6a80d7798fdb47fe91
// Raise an exception to reject the promise and trigger the outer .catch()
// handler. By default, an error response status (4xx, 5xx) does NOT cause the
// promise to reject!
function handleResponse(response, options) {
  return response.text().then((data) => {
    try {
      data = data && JSON.parse(data)
      data = keysToCamel(data)
    } catch {
      const error = response.ok ?
        TypeError("fetch did not return a valid JSON response") :
        Error(`Ajax ${options.method} error (${response.status})`)
      // Only track 5xx errors, not 4xx client errors
      if (response.ok || response.status >= 500) {
        trackError(error, response, data, options)
      }
      // removes cached promise for CSRF token if the server responds with 422
      if (response.status === 422) resetPromise()
      return Promise.reject(error)
    }
    if (response.ok) return data
    return Promise.reject(data)
  })
}

function handleConnectionFailure(e) {
  const error = e instanceof Error ? e : new Error(e)
  return Promise.reject(error)
}

function trackError(error, response, data, options) {
  if (!response.status) return

  errorReporter.warn(error, {
    status: response.status,
    ajaxUrl: response.url,
    responseData: data,
    csrfTokenFromDoc: getCsrfTokenFromDoc(),
    ...options
  })
}

export default ajax
